import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import JobApply, { JobApplyContainer }
  from '@fuww/library/src/JobApply';
import FormSubmitConfirmation
  from '@fuww/library/src/FormSubmitConfirmation';
import messages from '../../../lib/messages.json';
import throw404 from '../../../lib/throw404';
import Head from '../../Head';
import LinkAndAnchor from '../../LinkAndAnchor';
import ApplyForm from './ApplyForm';
import HomeLink from '../../HomeLink';
import getJobDetails from '../../../lib/getJobDetails';
import { Router } from '../../../routes.mjs';
import { useSiteContext } from '../../SiteContext';
import getDescription from '../JobDetail/getDescription';
import getJobsRoute from '../../../lib/getJobsRoute';
import REDIRECT_TIMEOUT from '../../../lib/redirectTimeout';
import { getRouterState } from '../../../lib/savedRouterState';
import BackButton from '../JobDetail/BackButton';
import { Job } from '../../../lib/graphql/api/graphql';

const getBreadcrumbLinks = ({
  title, locale, id, slug,
}: {
  title: string;
  locale: string;
  id: string;
  slug: string;
}) => [
  <HomeLink />,
  <LinkAndAnchor route={`jobs.${locale}`}>
    Fashion Jobs
  </LinkAndAnchor>,
  <LinkAndAnchor route={`job.${locale}`} params={{ id, slug }}>
    {title}
  </LinkAndAnchor>,
];

const JobApplication = ({ data: { job } }: {
  data: { job?: Job };
}) => {
  const intl = useIntl();
  const { locale, isCareerPage } = useSiteContext();
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [lastJobsQuery, setLastJobsQuery] = useState({});

  useEffect(() => {
    setLastJobsQuery({ ...getRouterState('jobs')?.query });
  }, []);

  useEffect(() => {
    if (uploadSuccess) {
      setTimeout(() => {
        Router.pushRoute(
          getJobsRoute(lastJobsQuery, locale),
          { ...lastJobsQuery },
        );
      }, REDIRECT_TIMEOUT);
    }
  }, [uploadSuccess, locale, lastJobsQuery]);

  if (!job?.active) {
    return throw404();
  }

  const {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    title, _id, id, slug,
  } = job;

  const breadcrumbLinks = isCareerPage ? [] : getBreadcrumbLinks({
    title,
    locale,
    id: String(id),
    slug,
  });

  const applyTitle = (
    <>
      {`${intl.formatMessage(messages['applicantForm.title'])}:`}
      <br />
      {title}
    </>
  );

  const applyForm = (
    <ApplyForm
      jobId={id}
      setUploadSuccess={setUploadSuccess}
    />
  );

  if (uploadSuccess) {
    return (
      <>
        <Head
          title={title}
          description={getDescription(job, intl)}
          subjectType="Job"
          subjectId={_id}
        />
        <JobApplyContainer
          title={<>title</>}
          breadcrumbLinks={breadcrumbLinks}
        >
          <FormSubmitConfirmation
            successHeadline={intl.formatMessage(messages.thankYou)}
            successMessage={
              intl.formatMessage(messages['applicantForm.applicationSent'])
            }
            redirectMessage={
              intl.formatMessage(messages['applicantForm.backToJobBoard'])
            }
          />
        </JobApplyContainer>
      </>
    );
  }

  return (
    <>
      <Head
        title={title}
        description={getDescription(job, intl)}
        subjectType="Job"
        subjectId={_id}
      />
      <JobApplyContainer
        title={applyTitle}
        backButton={(
          <BackButton job={job} showIcon={false} />
        )}
        breadcrumbLinks={breadcrumbLinks}
      >
        <JobApply
          jobDetails={getJobDetails(job, intl, locale)}
          privacyDisclaimer={
            intl.formatMessage(messages['applicantForm.disclaimer'])
          }
        >
          {applyForm}
        </JobApply>
      </JobApplyContainer>
    </>
  );
};

export default JobApplication;
